import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, updateDoc, doc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import CampusHeader from '../../components/feature/CampusHeader';
import Footer from '../../components/feature/Footer';
import { createConversation } from '../../services/messaging';
import { getUserProfile, getUserRole } from '../../services/auth';
import { releaseEscrowToSeller } from '../../services/wallet';
import { subscribeAuth } from '../../services/auth';
import { orderDeliveredEmail } from '../../services/email';

interface Order {
  id: string;
  buyerId: string;
  sellerId: string;
  status: 'payment_held' | 'delivery_started' | 'delivered' | 'completed' | 'cancelled';
  totalAmount: number;
  createdAt: any;
  orderItems?: Array<{
    listingId?: string;
    title?: string;
    price?: number;
    image?: string;
  }>;
  buyerName?: string;
  deliveryAddress?: string;
  buyerConfirmed?: boolean;
  sellerConfirmed?: boolean;
  escrowAmount?: number;
  sellerPayoutAmount?: number;
  platformFeeRate?: number;
}

const SellerOrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'active' | 'completed' | 'cancelled'>('all');
  const [orderSearch, setOrderSearch] = useState('');
  const [loadingOrderId, setLoadingOrderId] = useState<string | null>(null);
  const [viewType, setViewType] = useState<'all' | 'sales' | 'purchases'>('all');
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');

  // Helper function to get seller platform fee rate
  const getSellerPlatformFeeRate = async (_sellerId: string): Promise<number> => {
    // Platform no longer takes a percentage cut from sales.
    return 0;
  };

  useEffect(() => {
    const unsub = subscribeAuth(async (user) => {
      try {
        if (!user) {
          navigate('?login=1', { replace: true });
          setLoading(false);
          return;
        }
        // Role guard: only sellers should access this page
        const { role } = await getUserRole(user.uid);
        if (role !== 'seller') {
          navigate('/orders', { replace: true });
          setLoading(false);
          return;
        }

        setCurrentUserId(user.uid);
        const qSales = query(
          collection(db, 'orders'),
          where('sellerId', '==', user.uid),
          orderBy('createdAt', 'desc')
        );
        const qPurchases = query(
          collection(db, 'orders'),
          where('buyerId', '==', user.uid),
          orderBy('createdAt', 'desc')
        );
        const [qsSales, qsPurchases] = await Promise.all([getDocs(qSales), getDocs(qPurchases)]);
        const ordersData: Order[] = [];
        qsSales.forEach((d) => ordersData.push({ id: d.id, ...(d.data() as any) } as Order));
        qsPurchases.forEach((d) => ordersData.push({ id: d.id, ...(d.data() as any) } as Order));
        setOrders(ordersData);
      } catch (error) {
        console.error('Error loading orders:', error);
        alert('Failed to load orders. Please try again.');
      } finally {
        setLoading(false);
      }
    });
    return () => unsub();
  }, [navigate]);

  const filteredOrders = orders.filter(order => {
    const matchesView = viewType === 'all' ? true : (viewType === 'sales' ? order.sellerId === currentUserId : order.buyerId === currentUserId);
    const matchesFilter = filter === 'all' ? true : (filter === 'active' ? (order.status !== 'completed' && order.status !== 'cancelled') : order.status === filter);
    const matchesSearch = order.id.toLowerCase().includes(orderSearch.toLowerCase().trim());
    return matchesView && matchesFilter && (!orderSearch.trim() || matchesSearch);
  });
  const sortedOrders = [...filteredOrders].sort((a, b) => {
    const aTime = a.createdAt?.seconds ? a.createdAt.seconds : (a.createdAt?.toDate ? a.createdAt.toDate().getTime() / 1000 : 0);
    const bTime = b.createdAt?.seconds ? b.createdAt.seconds : (b.createdAt?.toDate ? b.createdAt.toDate().getTime() / 1000 : 0);
    return sortOrder === 'newest' ? bTime - aTime : aTime - bTime;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'payment_held': return 'bg-orange-100 text-orange-800';
      case 'delivery_started': return 'bg-blue-100 text-blue-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (order: Order) => {
    const { status, buyerConfirmed, sellerConfirmed } = order;
    if (status === 'delivered') {
      if (buyerConfirmed && sellerConfirmed) return 'Delivered • Awaiting Release';
      if (sellerConfirmed && !buyerConfirmed) return 'Seller Delivered • Awaiting Buyer';
      if (!sellerConfirmed && buyerConfirmed) return 'Buyer Received • Awaiting Seller';
    }
    switch (status) {
      case 'payment_held': return 'Payment Held';
      case 'delivery_started': return 'In Delivery';
      case 'delivered': return 'Delivered';
      case 'completed': return 'Completed';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  };

  const canMarkDelivered = (status: string) => status === 'delivery_started' || status === 'payment_held';

  const handleStartDelivery = async (order: Order) => {
    try {
      const orderRef = doc(db, 'orders', order.id);
      await updateDoc(orderRef, {
        status: 'delivery_started'
      });
      setOrders(prev => prev.map(o => o.id === order.id ? { ...o, status: 'delivery_started' } : o));
    } catch (error) {
      console.error('Error starting delivery:', error);
      alert('Failed to update order. Please try again.');
    }
  };

  const handleMarkDelivered = async (order: any) => {
    try {
      setLoadingOrderId(order.id);
      const orderRef = doc(db, 'orders', order.id);

      // Mark seller confirmed and delivered
      await updateDoc(orderRef, {
        status: 'delivered',
        sellerConfirmed: true,
      });

      // Refetch fresh order state from Firestore
      const snap = await getDoc(orderRef);
      let updated = { ...order, status: 'delivered', sellerConfirmed: true };
      if (snap.exists()) {
        const data = snap.data() as any;
        const sellerId = data.sellerId;
        const buyerId = data.buyerId;
        const totalAmount = Number(data.totalAmount || 0);

        if (sellerId && totalAmount > 0) {
          const platformFeeRate = await getSellerPlatformFeeRate(sellerId);
          const sellerPayoutAmount = Math.round(totalAmount * (1 - platformFeeRate));

          await updateDoc(orderRef, {
            status: 'completed',
            releasedAt: new Date(),
            platformFeeRate,
            sellerPayoutAmount,
          });
          await releaseEscrowToSeller(sellerId, totalAmount, order.id, platformFeeRate);

          updated = { ...updated, status: 'completed', platformFeeRate, sellerPayoutAmount };
          // Send delivery notifications to both parties
          try {
            const sellerProfile = await getUserProfile(sellerId);
            const buyerProfile = buyerId ? await getUserProfile(buyerId) : null;
            const sellerEmail = sellerProfile?.email || undefined;
            const buyerEmail = buyerProfile?.email || data?.buyerEmail || undefined;
            await orderDeliveredEmail(order.id, sellerEmail, buyerEmail);
          } catch (e) {
            console.warn('Failed to send order-delivered emails', e);
          }
          alert('Marked delivered. Payment released to your wallet.');
        } else {
          alert('Marked delivered. Missing seller or amount; cannot release.');
        }
      }

      setOrders(prev => prev.map(o => o.id === order.id ? updated : o));
    } catch (error) {
      console.error('Error marking delivered:', error);
      alert('Failed to update order. Please try again.');
    } finally {
      setLoadingOrderId(null);
    }
  };

  const handleMessageBuyer = async (order: Order) => {
    try {
      const profile = await getUserProfile(order.buyerId);
      const participantName = profile.fullName || order.buyerName || 'Buyer';
      const participantEmail = profile.email || '';
      const firstItem = order.orderItems?.[0];
      const conversationId = await createConversation(
        order.buyerId,
        participantName,
        participantEmail,
        firstItem?.listingId,
        firstItem?.title
      );
      navigate(`/messages?conversation=${conversationId}`);
    } catch (error) {
      console.error('Failed to message buyer', error);
      alert('Failed to start chat with buyer. Please try again.');
    }
  };

  const formatPrice = (price: number) => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(price);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <CampusHeader />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your orders...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <CampusHeader />
       
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Seller Orders</h1>
            <p className="text-gray-600 mt-2">Manage your sales and deliveries</p>
          </div>
          
          <div className="flex items-center gap-4">
            <input
              value={orderSearch}
              onChange={(e) => setOrderSearch(e.target.value)}
              placeholder="Search by Order ID"
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as any)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Orders</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <select
              value={viewType}
              onChange={(e) => setViewType(e.target.value as any)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All</option>
              <option value="sales">Sales</option>
              <option value="purchases">Purchases</option>
            </select>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as any)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="newest">Newest to Oldest</option>
              <option value="oldest">Oldest to Newest</option>
            </select>
          </div>
        </div>

        {filteredOrders.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-shopping-bag-line text-3xl text-gray-400"></i>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No orders yet</h3>
            <p className="text-gray-600 mb-6">Your sales will appear here once buyers order</p>
            <button
              onClick={() => navigate('/shop')}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              View Marketplace
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {filteredOrders.map((order) => (
              <div key={order.id} className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Order #{order.id?.slice(-8) || 'N/A'}
                    </h3>
                    <p className="text-gray-600 text-sm mt-1">
                      Placed on {order.createdAt ? new Date(order.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-sm ${getStatusColor(order.status)}`}>
                    {getStatusText(order)}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="md:col-span-2">
                    <div className="space-y-4">
                      {(order.orderItems || []).map((item, idx) => (
                        <div key={idx} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                          <div className="w-16 h-16 bg-white rounded-lg overflow-hidden border">
                            {item.image ? (
                              <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400">
                                <i className="ri-image-line"></i>
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{item.title || 'Item'}</div>
                            <div className="text-sm text-gray-600">{formatPrice(item.price || 0)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Total Amount</span>
                        <span className="font-semibold text-gray-900">{formatPrice(order.totalAmount || 0)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Order Status</span>
                        <span className={`px-2 py-1 rounded text-sm ${getStatusColor(order.status)}`}>{getStatusText(order)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Delivery Address</span>
                        <span className="text-gray-900">{order.deliveryAddress || 'N/A'}</span>
                      </div>
                    </div>

                    <div className="mt-6 grid grid-cols-2 gap-3">
                      {order.sellerId === currentUserId && canMarkDelivered(order.status) && (
                        <button
                          onClick={() => handleStartDelivery(order)}
                          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          Start Delivery
                        </button>
                      )}
                      {order.sellerId === currentUserId && (order.status === 'delivery_started' || order.status === 'delivered') && (
                        <button
                          onClick={() => handleMarkDelivered(order)}
                          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                          disabled={loadingOrderId === order.id}
                        >
                          {loadingOrderId === order.id ? 'Processing...' : 'Mark Delivered'}
                        </button>
                      )}
                      {order.sellerId === currentUserId && (
                        <button
                          onClick={() => handleMessageBuyer(order)}
                          className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                          Message Buyer
                        </button>
                      )}
                      <button
                        onClick={() => navigate(`/order/${order.id}`)}
                        className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default SellerOrdersPage;